// src/components/ErrorBoundary.jsx
import React from 'react';
import { trackError } from '@/lib/analytics';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a navigation-related error that we can handle gracefully
    const isNavigationError = 
      error.message?.includes('navigation') ||
      error.message?.includes('AnimatePresence') ||
      error.message?.includes('framer-motion') ||
      error.message?.includes('history') ||
      error.message?.includes('popstate') ||
      error.message?.includes('route') ||
      error.message?.includes('router') ||
      error.message?.includes('Cannot read properties of null') ||
      error.message?.includes('Cannot read properties of undefined') ||
      error.message?.includes('TypeError') ||
      error.message?.includes('ReferenceError') ||
      error.message?.includes('is not a function') ||
      error.message?.includes('is undefined') ||
      error.message?.includes('is null');

    if (isNavigationError) {
      console.warn('Navigation error detected, attempting recovery:', error.message);
      // Don't set hasError for navigation-related issues
      return null;
    }

    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a navigation-related error that we can handle gracefully
    const isNavigationError = 
      error.message?.includes('navigation') ||
      error.message?.includes('AnimatePresence') ||
      error.message?.includes('framer-motion') ||
      error.message?.includes('history') ||
      error.message?.includes('popstate') ||
      error.message?.includes('route') ||
      error.message?.includes('router') ||
      error.message?.includes('Cannot read properties of null') ||
      error.message?.includes('Cannot read properties of undefined') ||
      error.message?.includes('TypeError') ||
      error.message?.includes('ReferenceError') ||
      error.message?.includes('is not a function') ||
      error.message?.includes('is undefined') ||
      error.message?.includes('is null') ||
      errorInfo.componentStack?.includes('AnimatePresence') ||
      errorInfo.componentStack?.includes('motion') ||
      errorInfo.componentStack?.includes('Router') ||
      errorInfo.componentStack?.includes('ImageViewer') ||
      errorInfo.componentStack?.includes('ProgressiveImage');

    // If it's a navigation error, try to recover gracefully
    if (isNavigationError) {
      console.warn('Navigation error detected, attempting recovery:', error.message);
      
      // Try to recover by forcing a re-render
      setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 100);
      
      return;
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error', error, errorInfo);
    }

    // Track error in production
    try {
      trackError(error, {
        component: this.props.componentName || 'Unknown',
        errorInfo: errorInfo
      });
    } catch (trackingError) {
      console.warn('Error tracking failed:', trackingError);
    }
  }

  componentDidMount() {
    // Add global error handler for unhandled errors
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    // Clean up global error handlers
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleGlobalError = (event) => {
    const error = event.error;
    if (error && this.isRecoverableError(error)) {
      console.warn('Global error caught and recovered:', error.message);
      event.preventDefault();
    }
  };

  handleUnhandledRejection = (event) => {
    const error = event.reason;
    if (error && this.isRecoverableError(error)) {
      console.warn('Unhandled rejection caught and recovered:', error.message);
      event.preventDefault();
    }
  };

  isRecoverableError = (error) => {
    const errorMessage = error.message || error.toString();
    return errorMessage.includes('navigation') ||
           errorMessage.includes('AnimatePresence') ||
           errorMessage.includes('framer-motion') ||
           errorMessage.includes('history') ||
           errorMessage.includes('popstate') ||
           errorMessage.includes('route') ||
           errorMessage.includes('router') ||
           errorMessage.includes('Cannot read properties of null') ||
           errorMessage.includes('Cannot read properties of undefined') ||
           errorMessage.includes('TypeError') ||
           errorMessage.includes('ReferenceError') ||
           errorMessage.includes('is not a function') ||
           errorMessage.includes('is undefined') ||
           errorMessage.includes('is null');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
            <div className="text-6xl mb-4">😵</div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Refresh Page
            </button>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
